import React, { useState, useEffect, useRef, useMemo, useCallback, forwardRef, useImperativeHandle } from 'react';
import { 
  MagnifyingGlassIcon,
  XMarkIcon,
  UserCircleIcon,
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  ArrowPathIcon
} from "@heroicons/react/24/outline";
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Modal from '../common/Modal';
import Lottie from 'lottie-react';
import nvgtrLoader from '../../loaders/nvgtr_loader_white.json';
import nvgtrLoaderDark from '../../loaders/nvgtr_loader_dark.json';
import nvgtrLoaderWhite from '../../loaders/nvgtr_loader_white.json';
import { debounce } from 'lodash';
import { formatChatDate, groupChatsByDate } from '../../utils/date';
import { useTheme } from '../../features/theme/context/ThemeContext';
import './Sidebar.css';
import { useS3Image } from '../../hooks/useS3Image';

// Simple in-memory cache to replace the complex messageManager
const simpleCache = new Map();

// Helper function to get headers with test mode support
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  const headers = {
    Authorization: `Bearer ${token}`
  };
  
  // 🛠️ Development Test User/Org Override
  if (process.env.NODE_ENV === 'development') {
    const testOrgMode = localStorage.getItem('devTestOrgMode');
    const testOrgId = localStorage.getItem('devTestOrgId');
    const testUserId = localStorage.getItem('devActiveTestUserId');
    
    if (testOrgMode === 'true' && testOrgId) {
      headers['X-Test-Org-Id'] = testOrgId;
    }
    
    if (testOrgMode === 'true' && testUserId) {
      headers['X-Test-User-Id'] = testUserId;
    }
  }
  
  return headers;
};

export const MobileSidebar = forwardRef(({ 
  isOpen, 
  onClose, 
  user,
  setUser,
  activeChat,
  onChatSelect
}, ref) => {
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [longPressTimeout, setLongPressTimeout] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [editingChatId, setEditingChatId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [chatToDelete, setChatToDelete] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [chats, setChats] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const touchStartY = useRef(0);
  const chatListRef = useRef(null);
  const contextMenuRef = useRef(null);
  const lastFetchTime = useRef(0);
  const REFRESH_COOLDOWN = 500;
  const FETCH_COOLDOWN = 500;
  const LIMIT = 20;

  // Optimize chat filtering with useMemo
  const filteredChats = useMemo(() => {
    if (!searchTerm) return chats;
    const searchTermLower = searchTerm.toLowerCase();
    return chats.filter(chat => 
      (chat.title?.toLowerCase() || '').includes(searchTermLower) ||
      (chat.firstMessage?.toLowerCase() || '').includes(searchTermLower)
    );
  }, [chats, searchTerm]);

  const fetchChats = useCallback(async (shouldAppend = false) => {
    try {
      const now = Date.now();
      if (!shouldAppend && now - lastFetchTime.current < FETCH_COOLDOWN) {
        return;
      }

      // Use cached data first if not appending
      if (!shouldAppend) {
        const cachedChats = simpleCache.get('all_chats');
        if (cachedChats) {
          setChats(cachedChats);
          setIsLoading(false);
          // Only fetch if cache is old
          if (now - lastFetchTime.current > 30000) { // 30 seconds
            updateCacheInBackground();
          }
          return cachedChats;
        }
      }

      setIsLoading(true);

      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found');
        setIsLoading(false);
        return;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/chat`,
        { headers: getAuthHeaders() }
      );

      lastFetchTime.current = now;
      
      const newChats = Array.isArray(response.data) ? response.data : 
                      (response.data.chats || []);
      
      setChats(newChats);
      simpleCache.set('all_chats', newChats);

      // Prefetch first few chats
      newChats.slice(0, 3).forEach(chat => {
        prefetchChat(chat._id);
      });
      
      return newChats;
    } catch (error) {
      console.error('Error fetching chats:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const prefetchChat = useCallback(async (chatId) => {
    if (simpleCache.has(chatId)) return;

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/chat/${chatId}/messages?page=1`,
        { headers: getAuthHeaders() }
      );
      
      const messageData = Array.isArray(response.data) ? response.data : 
                         (response.data.messages || []);
      
      simpleCache.set(chatId, messageData);
    } catch (error) {
      console.error('Error prefetching chat:', error);
    }
  }, []);

  const updateCacheInBackground = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/chat`,
        { headers: getAuthHeaders() }
      );

      const newChats = Array.isArray(response.data) ? response.data : 
                      (response.data.chats || []);

      setChats(prevChats => {
        if (JSON.stringify(prevChats) !== JSON.stringify(newChats)) {
          simpleCache.set('all_chats', newChats);
          return newChats;
        }
        return prevChats;
      });

      // Prefetch updated chats
      newChats.slice(0, 3).forEach(chat => {
        prefetchChat(chat._id);
      });

    } catch (error) {
      console.error('Error updating cache:', error);
    }
  }, []);

  // Initial chat fetch
  useEffect(() => {
    if (isOpen) {
      const cachedChats = simpleCache.get('all_chats');
      if (cachedChats) {
        setChats(cachedChats);
        setIsLoading(false);
        // Update cache in background if it's old
        if (Date.now() - lastFetchTime.current > 30000) {
          updateCacheInBackground();
        }
      } else {
        fetchChats(false);
      }
    }
  }, [isOpen, fetchChats]);

  // Expose methods to parent
  useImperativeHandle(ref, () => ({
    fetchChats,
    getChats: () => chats,
    groupedChats: groupChatsByDate(filteredChats)
  }));

  // Debounce search to prevent excessive filtering
  const debouncedSetSearchTerm = useMemo(
    () => debounce((term) => setSearchTerm(term), 150),
    []
  );

  // Optimize refresh handling
  const handleRefresh = useCallback(async () => {
    const now = Date.now();
    if (now - lastFetchTime.current < REFRESH_COOLDOWN) {
      setPullDistance(0);
      return;
    }

    setIsRefreshing(true);
    lastFetchTime.current = now;

    try {
      await fetchChats(true);
    } catch (error) {
      console.error('Error refreshing chats:', error);
    } finally {
      setIsRefreshing(false);
      setPullDistance(0);
    }
  }, [fetchChats]);

  // Optimize touch handlers
  const handleTouchStart = useCallback((e) => {
    if (chatListRef.current?.scrollTop === 0) {
      touchStartY.current = e.touches[0].clientY;
    }
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (touchStartY.current === 0) return;

    const touchY = e.touches[0].clientY;
    const distance = Math.max(0, touchY - touchStartY.current);
    
    if (distance > 0) {
      e.preventDefault();
      setPullDistance(Math.min(distance, 30));
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (pullDistance >= 30) {
      handleRefresh();
    } else {
      setPullDistance(0);
    }
    touchStartY.current = 0;
  }, [pullDistance, handleRefresh]);

  // Handle chat selection with prefetching
  const handleChatClick = useCallback((chatId) => {
    if (editingChatId !== null) return;
    
    // Prefetch the selected chat if not in cache
    prefetchChat(chatId);
    
    // Prefetch the next chat in the list for smoother navigation
    const currentIndex = chats.findIndex(chat => chat._id === chatId);
    if (currentIndex >= 0 && currentIndex < chats.length - 1) {
      prefetchChat(chats[currentIndex + 1]._id);
    }
    
    onChatSelect(chatId);
    onClose();
  }, [editingChatId, onChatSelect, onClose, chats, prefetchChat]);

  const handleRenameSubmit = useCallback(async (chatId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_API_URL}/chat/${chatId}/title`,
        { title: editValue },
        { headers: getAuthHeaders() }
      );
      setEditingChatId(null);
      setEditValue('');
      fetchChats();
    } catch (error) {
      console.error('Error renaming chat:', error);
    }
  }, [editValue, fetchChats]);

  // Remove the effect that was updating filteredChats
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(event.target)) {
        setContextMenu(null);
      }
    };

    if (contextMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [contextMenu]);

  const handleNewChat = useCallback(() => {
    onChatSelect(null);
    onClose();
    navigate('/chat');
  }, [onChatSelect, onClose, navigate]);

  const handleProfileClick = useCallback(() => {
    onClose();
    window.dispatchEvent(new CustomEvent('open-account-modal', { detail: { tab: 'account' } }));
  }, [onClose]);

  const handleChatTouchStart = useCallback((e, chatId) => {
    e.preventDefault();
    const timer = setTimeout(() => {
      if (window.navigator.vibrate) {
        window.navigator.vibrate(50);
      }
      
      const touch = e.touches[0];
      const rect = e.target.getBoundingClientRect();
      
      setContextMenu({
        chatId,
        x: touch.clientX - rect.left,
        y: touch.clientY - rect.top
      });
    }, 500);
    
    setLongPressTimeout(timer);
  }, []);

  const handleChatTouchEnd = useCallback(() => {
    if (longPressTimeout) {
      clearTimeout(longPressTimeout);
      setLongPressTimeout(null);
    }
  }, [longPressTimeout]);

  const handleChatTouchMove = useCallback(() => {
    if (longPressTimeout) {
      clearTimeout(longPressTimeout);
      setLongPressTimeout(null);
    }
  }, [longPressTimeout]);

  const handleRename = useCallback(async (chatId, currentTitle) => {
    setEditingChatId(chatId);
    setEditValue(currentTitle);
    setContextMenu(null);
  }, []);

  const handleDeleteClick = useCallback((chatId) => {
    setChatToDelete(chatId);
    setDeleteModalOpen(true);
    setContextMenu(null);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/chat/${chatToDelete}`,
        { headers: getAuthHeaders() }
      );

      if (activeChat === chatToDelete) {
        onChatSelect(null);
      }

      fetchChats();
      setDeleteModalOpen(false);
      setChatToDelete(null);
    } catch (error) {
      console.error('Error deleting chat:', error);
    }
  }, [chatToDelete, activeChat, onChatSelect, fetchChats]);

  const formatChatTitle = (chat) => {
    if (chat.title) return chat.title;
    if (chat.messages && chat.messages[0]) {
      return chat.messages[0].content;
    }
    return 'New Chat';
  };

  // Handle profile image loading error
  const handleProfileImageError = async () => {
    try {
      if (!user?.profileImage?.key) return;
      
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/users/refresh-profile-image-url`,
        {
          headers: getAuthHeaders()
        }
      );
      
      if (response.data.success && response.data.profileImage) {
        // Update the user object with the new URL
        const updatedUser = { ...user, profileImage: response.data.profileImage };
        
        // Store in localStorage to persist across page refreshes
        localStorage.setItem('user', JSON.stringify(updatedUser));
        
        // Force a re-render by updating the state that contains the user
        // This requires the parent component to pass a setUser function
        if (window.updateUser) {
          window.updateUser(updatedUser);
        }
      }
    } catch (error) {
      console.error('Error refreshing profile image URL:', error);
    }
  };

  return (
    <>
      <div 
        className={`mobile-sidebar-root fixed inset-y-0 left-0 w-[280px] bg-white dark:bg-[#181818] transform transition-transform duration-300 ease-in-out md:hidden z-50 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{
          paddingTop: 'env(safe-area-inset-top)',
          paddingBottom: 15
        }}
      >
        <div className="flex flex-col h-full">
          {/* Search and New Chat */}
          <div className="px-4 pt-2 pb-1 flex items-center gap-2">
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 dark:text-[#939393]" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => debouncedSetSearchTerm(e.target.value)}
                className="bg-[#f0f0f0] dark:bg-[#282828] w-full pl-10 pr-4 py-2 rounded-[12px] border-[1px] border-[#C3C3C3] dark:border-[#444444] text-sm focus:outline-none font-['midus-sans'] font-normal placeholder:text-[#939393] placeholder:font-normal"
                placeholder="Search chat history"
                style={{ fontFamily: 'var(--chat-font-family)' }}
              />
              {searchTerm && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    debouncedSetSearchTerm('');
                  }}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  <XMarkIcon className="h-5 w-5 text-gray-400 dark:text-gray-600 " />
                </button>
              )}
            </div>
            <button
              onClick={handleNewChat}
              className="px-1.5 py-1.5  text-gray-500 dark:text-gray-400 hover:text-[#181818] dark:hover:text-gray-300 bg-[#f0f0f0] dark:bg-[#282828] border-[1px] border-[#C3C3C3] dark:border-[#444444] transition-colors hover:bg-gray-100 dark:hover:bg-[#282828] rounded-xl"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width={25} height={23} viewBox="0 0 16 16">
                <path fill={darkMode ? "#D9D9D9" : "black"} fillRule="evenodd" d="M6.169 6.331a3 3 0 0 0-.833 1.6l-.338 1.912a1 1 0 0 0 1.159 1.159l1.912-.338a3 3 0 0 0 1.6-.833l3.07-3.07l2-2A.9.9 0 0 0 15 4.13A3.13 3.13 0 0 0 11.87 1a.9.9 0 0 0-.632.262l-2 2zm3.936-1.814L7.229 7.392a1.5 1.5 0 0 0-.416.8L6.6 9.4l1.208-.213l.057-.01a1.5 1.5 0 0 0 .743-.406l2.875-2.876a1.63 1.63 0 0 0-1.378-1.378m2.558.199a3.14 3.14 0 0 0-1.379-1.38l.82-.82a1.63 1.63 0 0 1 1.38 1.38zM8 2.25a.75.75 0 0 0-.75-.75H4.5a3 3 0 0 0-3 3v7a3 3 0 0 0 3 3h7a3 3 0 0 0 3-3V8.75a.75.75 0 0 0-1.5 0v2.75a1.5 1.5 0 0 1-1.5 1.5h-7A1.5 1.5 0 0 1 3 11.5v-7A1.5 1.5 0 0 1 4.5 3h2.75A.75.75 0 0 0 8 2.25" clipRule="evenodd"/>
              </svg>
            </button>
          </div>

          {/* Chat List */}
          <div 
            ref={chatListRef}
            className="flex-1 overflow-y-auto"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {/* Pull to refresh indicator */}
            <div className={`flex justify-center items-center ${pullDistance > 0 ? 'h-12' : 'h-0'} transition-all duration-300 overflow-hidden`}>
              {isRefreshing ? (
                <Lottie
                  animationData={darkMode ? nvgtrLoaderWhite : nvgtrLoader}
                  loop={true}
                  style={{ width: 40, height: 40 }}
                />
              ) : pullDistance > 0 ? (
                <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                  <ArrowPathIcon className={`w-5 h-5 transition-transform duration-300 ${pullDistance >= 30 ? 'rotate-180' : ''}`} />
                  <span className="text-sm">Pull to refresh</span>
                </div>
              ) : null}
            </div>

            {/* Show loader when loading */}
            {isLoading && !isRefreshing ? (
              <div className="flex justify-center items-center h-[50vh]">
                <Lottie
                  animationData={darkMode ? nvgtrLoaderWhite : nvgtrLoader}
                  loop={true}
                  style={{ width: 40, height: 40 }}
                />
              </div>
            ) : (
              /* Grouped Chat History */
              <div className="px-2">
                {filteredChats.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full px-6 text-center">
                    <p className="text-[13px] text-gray-400 dark:text-[#555]">
                      {searchTerm ? 'No matching chats' : 'Your conversations will appear here'}
                    </p>
                  </div>
                ) : (
                  groupChatsByDate(filteredChats).map(([group, groupChats]) => (
                    <div key={group} className="mt-4 first:mt-2">
                      <div className="px-2 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 select-none">
                        {group}
                      </div>
                      {groupChats.map((chat) => (
                        <div key={chat._id} className="relative">
                          <button
                            onTouchStart={(e) => handleChatTouchStart(e, chat._id)}
                            onTouchEnd={handleChatTouchEnd}
                            onTouchMove={handleChatTouchMove}
                            onClick={() => handleChatClick(chat._id)}
                            className={`w-full text-left px-2 py-2.5 rounded-xl transition-colors ${
                              activeChat === chat._id ? 'bg-gray-100 dark:bg-[#282828]' : 'hover:bg-gray-50 dark:hover:bg-[#282828]'
                            }`}
                          >
                            <div className="text-sm font-medium truncate">
                              <span className="text-[14px] truncate" style={{ fontFamily: 'var(--chat-font-family)' }}>
                                {editingChatId === chat._id ? (
                                  <input
                                    type="text"
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                    onBlur={() => handleRenameSubmit(chat._id)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        handleRenameSubmit(chat._id);
                                      }
                                    }}
                                    className="bg-transparent w-full focus:outline-none"
                                    style={{ fontFamily: 'var(--chat-font-family)' }}
                                    autoFocus
                                  />
                                ) : (
                                  formatChatTitle(chat)
                                )}
                              </span>
                            </div>
                          </button>

                          {/* Context Menu */}
                          {contextMenu?.chatId === chat._id && (
                            <div 
                              ref={contextMenuRef}
                              className="absolute left-0 right-0 bg-white dark:bg-[#282828] shadow-lg rounded-lg border border-gray-200 dark:border-gray-600 z-50 mt-2 mx-2"
                              style={{
                                top: contextMenu.y
                              }}
                            >
                              <button
                                onClick={() => handleRename(chat._id, formatChatTitle(chat))}
                                className="w-full px-4 py-3 text-left text-sm hover:bg-gray-50 dark:hover:bg-[#282828] flex items-center gap-2"
                              >
                                <PencilSquareIcon className="w-4 h-4" />
                                <span className="select-none">Rename</span>
                              </button>
                              <button
                                onClick={() => handleDeleteClick(chat._id)}
                                className="w-full px-4 py-3 text-left text-sm text-red-500 dark:text-red-400 hover:bg-gray-50 dark:hover:bg-[#282828] flex items-center gap-2"
                              >
                                <TrashIcon className="w-4 h-4" />
                                <span className="select-none">Delete</span>
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Profile */}
          <div className="p-2 pb-4 border-t border-gray-200 dark:border-[#444444]">
            <button
              onClick={handleProfileClick}
              className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-[#282828] transition-colors w-full"
            >
              <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-[#282828] flex items-center justify-center">
                {user?.profileImage?.url ? (
                  <img 
                    src={user.profileImage.url} 
                    alt="Profile" 
                    className="w-full h-full rounded-full object-cover"
                    onError={handleProfileImageError}
                  />
                ) : (
                  <UserCircleIcon className="w-6 h-6 text-gray-400 dark:text-gray-600" />
                )}
              </div>
              <div className="text-left flex-1">
                <div className="text-sm font-medium text-[#181818] dark:text-gray-300" style={{ fontFamily: 'var(--chat-font-family)' }}>
                  {user?.firstName && user?.lastName ? 
                    `${user.firstName} ${user.lastName}` 
                    : 'User'}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {user?.email || 'View Profile'}
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setChatToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Chat"
        message="This can't be undone. Any created memories will be retained."
        confirmText="Delete"
        cancelText="Cancel"
      />
    </>
  );
}); 